import React, { useState } from 'react';
import { Download, FileDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/Button';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { cn } from '../../../lib/utils';
import './ExportSettings.css';

export function ExportSettings({ isOpen, onClose, onExport, report }) {
  const { t } = useTranslation();
  const [format, setFormat] = useState('PDF');
  const [includeOptions, setIncludeOptions] = useState({
    allModules: true,
    evidenceOnly: false,
    aiInsights: true,
    agronomistReviews: true,
    comments: false,
    activityLog: false,
  });
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    await onExport(format, includeOptions);
    setExporting(false);
  };

  const toggleIncludeOption = (key) => {
    setIncludeOptions(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Dialog.Dialog open={isOpen} onOpenChange={onClose}>
      <Dialog.DialogContent className="export-settings-modal" aria-describedby={undefined}>
        <Dialog.DialogHeader className="export-settings-modal__header">
          <div className="export-settings-modal__header-icon">
            <FileDown size={20} />
          </div>
          <Dialog.DialogTitle className="export-settings-modal__title">
            {t('reports.exportSettings.title')}
          </Dialog.DialogTitle>
        </Dialog.DialogHeader>

        <div className="export-settings-modal__content">
          {/* Format Selection */}
          <div className="export-settings-modal__section">
            <Label className="export-settings-modal__label">{t('reports.exportSettings.exportFormat')}</Label>
            <RadioGroup value={format} onValueChange={setFormat} className="export-settings-modal__radio-group">
              <div 
                className={cn(
                  "export-settings-modal__radio-option",
                  format === 'PDF' && "export-settings-modal__radio-option--selected"
                )}
                onClick={() => setFormat('PDF')}
              >
                <RadioGroupItem value="PDF" id="format-pdf" />
                <Label htmlFor="format-pdf" className="export-settings-modal__radio-label">
                  {t('reports.exportSettings.formats.pdfRecommended')}
                </Label>
                <span className="export-settings-modal__radio-badge">
                  {t('reports.exportSettings.recommended', 'Recommended')}
                </span>
              </div>
              <div 
                className={cn(
                  "export-settings-modal__radio-option",
                  format === 'DOCX' && "export-settings-modal__radio-option--selected"
                )}
                onClick={() => setFormat('DOCX')}
              >
                <RadioGroupItem value="DOCX" id="format-docx" />
                <Label htmlFor="format-docx" className="export-settings-modal__radio-label">
                  {t('reports.exportSettings.formats.wordDocx')}
                </Label>
              </div>
              <div 
                className={cn(
                  "export-settings-modal__radio-option",
                  format === 'LATEX' && "export-settings-modal__radio-option--selected"
                )}
                onClick={() => setFormat('LATEX')}
              >
                <RadioGroupItem value="LATEX" id="format-latex" />
                <Label htmlFor="format-latex" className="export-settings-modal__radio-label">
                  {t('reports.exportSettings.formats.latex')}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Include Options */}
          <div className="export-settings-modal__section">
            <Label className="export-settings-modal__label">{t('reports.exportSettings.include')}</Label>
            <div className="export-settings-modal__checkbox-group">
              <label className="export-settings-modal__checkbox-item" htmlFor="include-all-modules">
                <Checkbox
                  id="include-all-modules"
                  checked={includeOptions.allModules}
                  onCheckedChange={() => toggleIncludeOption('allModules')}
                />
                <span className="export-settings-modal__checkbox-label">
                  {t('reports.exportSettings.options.allModules')}
                </span>
              </label>
              <label className="export-settings-modal__checkbox-item" htmlFor="include-evidence">
                <Checkbox
                  id="include-evidence"
                  checked={includeOptions.evidenceOnly}
                  onCheckedChange={() => toggleIncludeOption('evidenceOnly')}
                />
                <span className="export-settings-modal__checkbox-label">
                  {t('reports.exportSettings.options.evidenceOnly')}
                </span>
              </label>
              <label className="export-settings-modal__checkbox-item" htmlFor="include-ai">
                <Checkbox
                  id="include-ai"
                  checked={includeOptions.aiInsights}
                  onCheckedChange={() => toggleIncludeOption('aiInsights')}
                />
                <span className="export-settings-modal__checkbox-label">
                  {t('reports.exportSettings.options.aiInsights')}
                </span>
              </label>
              <label className="export-settings-modal__checkbox-item" htmlFor="include-reviews">
                <Checkbox
                  id="include-reviews"
                  checked={includeOptions.agronomistReviews}
                  onCheckedChange={() => toggleIncludeOption('agronomistReviews')}
                />
                <span className="export-settings-modal__checkbox-label">
                  {t('reports.exportSettings.options.agronomistReviews')}
                </span>
              </label>
              <label className="export-settings-modal__checkbox-item" htmlFor="include-comments">
                <Checkbox
                  id="include-comments"
                  checked={includeOptions.comments}
                  onCheckedChange={() => toggleIncludeOption('comments')}
                />
                <span className="export-settings-modal__checkbox-label">
                  {t('reports.exportSettings.options.comments')}
                </span>
              </label>
              <label className="export-settings-modal__checkbox-item" htmlFor="include-activity">
                <Checkbox
                  id="include-activity"
                  checked={includeOptions.activityLog}
                  onCheckedChange={() => toggleIncludeOption('activityLog')}
                />
                <span className="export-settings-modal__checkbox-label">
                  {t('reports.exportSettings.options.activityLog')}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="export-settings-modal__actions">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={exporting}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon={<Download size={18} />}
            onClick={handleExport}
            loading={exporting}
          >
            {t('common.export')}
          </Button>
        </div>
      </Dialog.DialogContent>
    </Dialog.Dialog>
  );
}

